import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axiosPrivate from 'config/axiosPrivate';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';

import ProjectTextsTable from 'components/shared/projecttexttable/ProjectTextsTable';
import ProjectDetailsCard from 'components/shared/ProjectDetailsCard';
import { Loader } from 'components/shared/Loader';
import ErrorComponent from 'components/shared/ErrorComponent';
import usePageTracking from 'utils/hooks/usePageTracking';

const PROJECT_MANAGER_PROJECT_DETAILS_PAGE = '/ProjectManager/DetailsProject';

export default function ProjectManagerDetailsProject() {
  usePageTracking(PROJECT_MANAGER_PROJECT_DETAILS_PAGE);

  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [selectedWebsiteUrl] = useWebsiteFilter('PM_Dashboard_SelectedWebsite');

  const [order, setOrder] = useState(null);
  const [textList, setTextList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isTextLoading, setIsTextLoading] = useState(true);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const [statusBanner, setStatusBanner] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState('');
  const [websiteDomain, setWebsiteDomain] = useState('');

  useEffect(() => {
    document.title = t('Typix - Détails du projet');
    const previousBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#FAFAFA';
    window.dispatchEvent(new Event('locationchange'));

    return () => {
      document.body.style.backgroundColor = previousBodyBg;
    };
  }, [t]);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        let projectId = urlParams.get('id');
        if (!projectId) {
          projectId = window.localStorage.getItem('ProjectManager_ProjectDisplay_id');
        }

        if (projectId) {
          setIsLoading(true);
          setIsError(false);
          const response = await axiosPrivate.get(`/project/${projectId}`);
          if (response.status === 200) {
            setOrder(response.data);
            try {
              const { fetchadminCLientOrderDisplay, fetchadminCLientOrderLineDisplay } = await import('store/AdminClient/OrderDisplay/features');
              dispatch(fetchadminCLientOrderDisplay(projectId));
              dispatch(fetchadminCLientOrderLineDisplay(projectId));
            } catch { }
          }
        }
      } catch (error) {
        setIsError(true);
        console.error('Error fetching project (PM):', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectData();
  }, [dispatch]);

  useEffect(() => {
    const fetchTexts = async () => {
      if (!order?.id) return;

      try {
        setIsTextLoading(true);
        const response = await axiosPrivate.get(`/texts/external/by-project/${order.id}`);
        if (response.status === 200) {
          setTextList(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching texts:', error);
        setTextList([]);
      } finally {
        setIsTextLoading(false);
      }
    };

    fetchTexts();
  }, [order?.id]);

  useEffect(() => {
    const toDomain = (u) =>
      (u || '').replace(/^https?:\/\//i, '').replace(/\/.*$/, '').trim();

    const websiteUrl = order?.website?.urlSite || order?.website?.url || selectedWebsiteUrl || '';

    if (!websiteUrl) {
      setFaviconUrl('');
      setWebsiteDomain('');
      return;
    }

    const domain = toDomain(websiteUrl);
    setWebsiteDomain(domain);

    if (!domain) {
      setFaviconUrl('');
      return;
    }

    const url = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(
      domain
    )}&sz=64`;
    setFaviconUrl(url);
  }, [order?.website?.urlSite, order?.website?.url, selectedWebsiteUrl]);

  useEffect(() => {
    if (!statusBanner) return;
    const timeoutId = setTimeout(() => setStatusBanner(null), 2500);
    return () => clearTimeout(timeoutId);
  }, [statusBanner]);

  const handleStatusChange = (textId, nextStatus) => {
    const statusFlags = {
      Brouillon: {
        status: 'Brouillon',
        delivered: false,
        validated: false,
        deployed: false,
      },
      Livré: {
        status: 'Livré',
        delivered: true,
        validated: false,
        deployed: false,
      },
      Validé: {
        status: 'Validé',
        delivered: true,
        validated: true,
        deployed: false,
      },
      'En ligne': {
        status: 'En ligne',
        delivered: true,
        validated: true,
        deployed: true,
      },
    };

    const flags = statusFlags[nextStatus] || statusFlags.Brouillon;

    setTextList((prev) =>
      Array.isArray(prev)
        ? prev.map((text) =>
          text.id === textId
            ? {
              ...text,
              status: flags.status,
              delivered: flags.delivered,
              validated: flags.validated,
              deployed: flags.deployed,
            }
            : text
        )
        : prev
    );
  };

  const totalTextsCount = Array.isArray(textList) ? textList.length : 0;
  const onlineTextsCount = Array.isArray(textList)
    ? textList.filter((t) => t?.deployed).length
    : 0;
  const validatedTextsCount = Array.isArray(textList)
    ? textList.filter((t) => t?.validated && !t?.deployed).length
    : 0;
  const deliveredTextsCount = Array.isArray(textList)
    ? textList.filter((t) => t?.delivered && !t?.validated).length
    : 0;
  const draftTextsCount = Array.isArray(textList)
    ? textList.filter(
      (t) => !t?.delivered && !t?.validated && !t?.deployed
    ).length
    : 0;

  if (isLoading) return <Loader />;
  if (isError || !order) return <ErrorComponent />;

  return (
    <div style={{ backgroundColor: '#FAFAFA' }}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
      <div
        style={{
          padding: '6px 16px',
          marginTop: '-15px',
          marginLeft: '-18px',
          borderBottom: '1px solid #E0E0E0',
          fontSize: '14px',
          color: '#9E9E9E',
        }}
      >
        <div style={{ marginBottom: '8px' }}>
          <button
            type="button"
            onClick={() => history.goBack()}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              fontWeight: 500,
              color: '#000000',
              cursor: 'pointer',
            }}
          >
            {t('Projet')}
          </button>
          <span> &gt; </span>
          <span>{websiteDomain || order?.website?.urlSite || ''}</span>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '12px 16px',
          marginLeft: '-10px',
          marginTop: '15px',
          marginBottom: '-18px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontSize: '35px',
                fontWeight: 600,
                color: '#000000',
              }}
            >
              {order?.title || ''}
            </span>

            {order?.type && (
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: '999px',
                  border: '1px solid #E0E0E0',
                  fontSize: '11px',
                  color: '#757575',
                  backgroundColor: '#FFFFFF',
                  lineHeight: 1.4,
                }}
              >
                {order.type}
              </span>
            )}
          </div>

          {websiteDomain && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                marginTop: 4,
                marginLeft: '-10px',
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {faviconUrl ? (
                  <img
                    src={faviconUrl}
                    alt={websiteDomain}
                    style={{ width: 44, height: 44, borderRadius: 10 }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 600,
                      color: '#FFFFFF',
                    }}
                  >
                    {(websiteDomain[0] || 'W').toUpperCase()}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {websiteDomain}
                </span>
                {totalTextsCount > 0 && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: '#6B7280',
                      marginTop: 2,
                    }}
                  >
                    {t('{{online}} textes en lignes sur {{total}}', {
                      online: onlineTextsCount,
                      total: totalTextsCount,
                    })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isTextLoading ? (
        <div style={{ padding: '32px 16px' }}>
          <Loader />
        </div>
      ) : (
        Array.isArray(textList) &&
        (isDetailsOpen ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 24,
              padding: '0 16px 32px',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <ProjectTextsTable
                texts={textList}
                onStatusChangeBanner={setStatusBanner}
                onNewContentClick={null}
                isDetailsOpen={isDetailsOpen}
                orderId={order?.id}
                generationMessage=""
                orderTextAmount={order?.textAmount}
                totalTextsCount={totalTextsCount}
                onStatusChange={handleStatusChange}
              />

              {statusBanner && (
                <div
                  style={{
                    marginTop: 32,
                    padding: '10px 16px',
                    borderRadius: '8px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 4px 10px rgba(15,23,42,0.08)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13,
                    color: '#111827',
                    marginLeft: '500px',
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: '#111827',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      fontSize: 11,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </div>
                  <span>
                    {t('Statut modifié : {{status}}', { status: statusBanner })}
                  </span>
                </div>
              )}
            </div>

            <ProjectDetailsCard
              order={order}
              isOpen={isDetailsOpen}
              onToggle={setIsDetailsOpen}
              onlineTextsCount={onlineTextsCount}
              totalTextsCount={totalTextsCount}
              validatedTextsCount={validatedTextsCount}
              deliveredTextsCount={deliveredTextsCount}
              draftTextsCount={draftTextsCount}
              hideAttribution={false}
            />
          </div>
        ) : (
          <div
            style={{
              position: 'relative',
              padding: '0 16px 32px',
            }}
          >
            <ProjectTextsTable
              texts={textList}
              onStatusChangeBanner={setStatusBanner}
              onNewContentClick={null}
              isDetailsOpen={isDetailsOpen}
              orderId={order?.id}
              generationMessage=""
              orderTextAmount={order?.textAmount}
              totalTextsCount={totalTextsCount}
              onStatusChange={handleStatusChange}
            />

            {statusBanner && (
              <div
                style={{
                  marginTop: 16,
                  padding: '10px 16px',
                  borderRadius: 999,
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0 4px 10px rgba(15,23,42,0.08)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 13,
                  color: '#111827',
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFFFFF',
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                >
                  ✓
                </div>
                <span>
                  {t('Statut modifié : {{status}}', { status: statusBanner })}
                </span>
              </div>
            )}

            <div
              style={{
                position: 'absolute',
                top: -64,
                right: 16,
              }}
            >
              <ProjectDetailsCard
                order={order}
                isOpen={isDetailsOpen}
                onToggle={setIsDetailsOpen}
                onlineTextsCount={onlineTextsCount}
                totalTextsCount={totalTextsCount}
                validatedTextsCount={validatedTextsCount}
                deliveredTextsCount={deliveredTextsCount}
                draftTextsCount={draftTextsCount}
                hideAttribution={false}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
